// src/components/negocios/MatchCardModal.jsx
import React, { useMemo, useEffect } from 'react';
import {
  X,
  Sparkles,
  ArrowRightLeft,
  Coins,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ClipboardList,
  Users,
  Home,
  MapPin,
  GitMerge,
} from 'lucide-react';

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function moneyBRL(v) {
  const n = toNumber(v, 0);
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function statusBadge(status = 'AMARELO') {
  const s = String(status || '').toUpperCase();
  if (s === 'VERDE') {
    return {
      label: 'Match forte',
      icon: CheckCircle2,
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
  }
  if (s === 'VERMELHO') {
    return {
      label: 'Inviável',
      icon: XCircle,
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
    };
  }
  return {
    label: 'Precisa ajuste',
    icon: AlertTriangle,
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
  };
}

function roleLabel(role) {
  const r = String(role || '').toUpperCase();
  if (r.includes('VENDEDOR_A')) return 'Vendedor A (Imóvel)';
  if (r.includes('COMPRADOR_B')) return 'Comprador B (Cliente)';
  if (r.includes('VENDEDOR_C')) return 'Vendedor C (Pivô)';
  return role || 'Participante';
}

function itemLabel(itemId, itens = []) {
  const list = safeArr(itens);
  const id = String(itemId || '');
  if (id === 'DINHEIRO') return 'Dinheiro / Financiamento';
  const found = list.find((x) => String(x?.id) === id);
  if (!found) return id;
  const subtipo = found?.subtipo || found?.tipo || 'Item';
  const bairro = found?.metadata?.bairro || found?.bairro || '';
  const cidade = found?.metadata?.cidade || found?.cidade || '';
  const loc = [bairro, cidade].filter(Boolean).join(' - ');
  return loc ? `${subtipo} (${loc})` : String(subtipo);
}

function iconForItem(item) {
  const tipo = String(item?.tipo || '').toUpperCase();
  if (tipo === 'IMOVEL') return Home;
  return Coins;
}

/**
 * Props
 * - open: boolean
 * - match: MatchResult (simulator)
 * - onClose: fn
 * - onOpenImovel: fn (idImovel)  (opcional)
 */
export default function MatchCardModal({ open, match, onClose, onOpenImovel }) {
  const vm = useMemo(() => {
    const tipo = String(
      match?.tipo_match || match?.tipo || 'DIRETO'
    ).toUpperCase();
    const status = String(match?.status || 'AMARELO').toUpperCase();
    const score = toNumber(match?.score_match ?? match?.score ?? 0, 0);
    const itens = safeArr(match?.itens);
    const transfers = safeArr(match?.transfers);
    const saldos = safeArr(match?.saldos);
    const pendencias = safeArr(match?.pendencias);
    const justificativas = safeArr(match?.justificativas);

    const alvo = match?.alvo || {};
    const idImovel = alvo?.id_imovel || null;
    const valorVenda = alvo?.valor_venda ?? null;

    const tornaTotal = toNumber(
      match?.torna?.total ?? match?.torna_total ?? 0,
      0
    );
    const entradaMin = toNumber(match?.torna?.entrada_minima_exigida ?? 0, 0);
    const entradaOk = match?.torna?.entrada_minima_ok ?? true;

    const participantes = safeArr(match?.participantes);

    return {
      tipo,
      status,
      score,
      itens,
      transfers,
      saldos,
      pendencias,
      justificativas,
      alvo: { ...alvo, idImovel, valorVenda },
      torna: { total: tornaTotal, entradaMin, entradaOk },
      participantes,
    };
  }, [match]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const badge = statusBadge(vm.status);
  const BadgeIcon = badge.icon;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={() => onClose?.()}
      />

      {/* modal */}
      <div className="relative z-[81] w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl">
        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-zinc-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-800">
                <GitMerge className="h-3.5 w-3.5" />
                {vm.tipo === 'TRIANGULAR'
                  ? 'Triangular (3 partes)'
                  : 'Direto (2 partes)'}
              </span>

              <span
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${badge.cls}`}
              >
                <BadgeIcon className="h-3.5 w-3.5" />
                {badge.label}
              </span>

              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-800">
                <Sparkles className="h-3.5 w-3.5" />
                Score: {Math.round(vm.score)}
              </span>

              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-800">
                <Coins className="h-3.5 w-3.5" />
                Torna: {moneyBRL(vm.torna.total)}
              </span>
            </div>

            <div className="mt-2 text-sm font-extrabold text-zinc-900">
              Detalhes do Match
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-zinc-600">
              {vm.alvo?.idImovel && (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 px-2 py-1">
                    <Home className="h-3.5 w-3.5" />
                    Imóvel:{' '}
                    <span className="font-semibold">{vm.alvo.idImovel}</span>
                  </span>
                </>
              )}
              {vm.alvo?.valorVenda != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 px-2 py-1">
                  <Coins className="h-3.5 w-3.5" />
                  Valor:{' '}
                  <span className="font-semibold">
                    {moneyBRL(vm.alvo.valorVenda)}
                  </span>
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => onClose?.()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* content */}
        <div className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-[1fr_360px]">
          {/* LEFT */}
          <div className="space-y-4">
            {/* transfers */}
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4 text-zinc-700" />
                  <div className="text-sm font-extrabold text-zinc-900">
                    Transfers (fluxo da troca)
                  </div>
                </div>
                <span className="rounded-full bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-700">
                  {vm.transfers.length} item(ns)
                </span>
              </div>

              {vm.transfers.length === 0 ? (
                <div className="p-4 text-sm text-zinc-600">
                  Nenhum transfer gerado.
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {vm.transfers.map((t, idx) => (
                    <div key={idx} className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-zinc-900">
                          {itemLabel(t?.item_id, vm.itens)}
                        </div>
                        <div className="text-sm font-extrabold text-emerald-700">
                          {moneyBRL(t?.valor_troca)}
                        </div>
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-zinc-600">
                        <span className="rounded-full bg-zinc-50 px-2 py-1">
                          de:{' '}
                          <span className="font-semibold">
                            {t?.from_owner_id}
                          </span>
                        </span>
                        <span className="rounded-full bg-zinc-50 px-2 py-1">
                          para:{' '}
                          <span className="font-semibold">
                            {t?.to_owner_id}
                          </span>
                        </span>
                        {t?.descricao && (
                          <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">
                            {t.descricao}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* saldos */}
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-zinc-700" />
                  <div className="text-sm font-extrabold text-zinc-900">
                    Saldos
                  </div>
                </div>
              </div>

              {vm.saldos.length === 0 ? (
                <div className="p-4 text-sm text-zinc-600">Sem saldos.</div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
                  {vm.saldos.map((s, idx) => {
                    const saldoFinal = toNumber(s?.saldo_final, 0);
                    const isOk = Math.abs(saldoFinal) < 0.01;
                    return (
                      <div
                        key={idx}
                        className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-extrabold text-zinc-900">
                            {s?.owner_id}
                          </div>
                          <span
                            className={[
                              'rounded-full border px-2 py-1 text-[11px] font-semibold',
                              isOk
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-amber-200 bg-amber-50 text-amber-700',
                            ].join(' ')}
                          >
                            saldo: {moneyBRL(saldoFinal)}
                          </span>
                        </div>

                        <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                          <div className="rounded-xl bg-white p-2">
                            <div className="text-[11px] font-semibold text-zinc-500">
                              Recebe
                            </div>
                            <div className="font-extrabold text-zinc-900">
                              {moneyBRL(s?.recebe)}
                            </div>
                          </div>
                          <div className="rounded-xl bg-white p-2">
                            <div className="text-[11px] font-semibold text-zinc-500">
                              Entrega
                            </div>
                            <div className="font-extrabold text-zinc-900">
                              {moneyBRL(s?.entrega)}
                            </div>
                          </div>
                        </div>

                        {safeArr(s?.detalhe).length > 0 && (
                          <div className="mt-2 space-y-1 text-[12px] text-zinc-700">
                            {safeArr(s.detalhe)
                              .slice(0, 5)
                              .map((d, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between gap-2"
                                >
                                  <span className="truncate">
                                    {itemLabel(d?.item, vm.itens)}
                                  </span>
                                  <span className="font-semibold text-zinc-900">
                                    {moneyBRL(d?.valor)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-4">
            {/* participantes */}
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-zinc-700" />
                  <div className="text-sm font-extrabold text-zinc-900">
                    Participantes
                  </div>
                </div>
              </div>

              {vm.participantes.length === 0 ? (
                <div className="p-4 text-sm text-zinc-600">
                  Sem participantes.
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {vm.participantes.map((p, idx) => (
                    <div key={idx} className="px-4 py-3">
                      <div className="text-[11px] font-semibold text-zinc-500">
                        {roleLabel(p?.role)}
                      </div>
                      <div className="text-sm font-extrabold text-zinc-900">
                        {p?.id}
                      </div>
                      <div className="text-[12px] text-zinc-600">
                        Tipo:{' '}
                        <span className="font-semibold">
                          {p?.tipo || 'PESSOA'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* itens */}
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-zinc-700" />
                  <div className="text-sm font-extrabold text-zinc-900">
                    Itens
                  </div>
                </div>
                <span className="rounded-full bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-700">
                  {vm.itens.length}
                </span>
              </div>

              {vm.itens.length === 0 ? (
                <div className="p-4 text-sm text-zinc-600">Sem itens.</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {vm.itens.map((it, idx) => {
                    const Icon = iconForItem(it);
                    const subtipo = it?.subtipo || it?.tipo || 'Item';
                    const bairro = it?.metadata?.bairro || it?.bairro || '';
                    const cidade = it?.metadata?.cidade || it?.cidade || '';
                    const loc = [bairro, cidade].filter(Boolean).join(' - ');
                    const valor =
                      it?.valor_troca ?? it?.valor_referencia ?? null;

                    return (
                      <div key={idx} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-50 text-zinc-700">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-extrabold text-zinc-900">
                                {subtipo}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-[12px] text-zinc-600">
                                {loc && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 px-2 py-1">
                                    <MapPin className="h-3 w-3" />
                                    {loc}
                                  </span>
                                )}
                                <span className="rounded-full bg-zinc-50 px-2 py-1">
                                  tipo:{' '}
                                  <span className="font-semibold">
                                    {it?.tipo}
                                  </span>
                                </span>
                                <span className="rounded-full bg-zinc-50 px-2 py-1">
                                  owner:{' '}
                                  <span className="font-semibold">
                                    {it?.owner_id}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            {valor != null && (
                              <div className="text-sm font-extrabold text-emerald-700">
                                {moneyBRL(valor)}
                              </div>
                            )}
                            <div className="mt-1 text-[11px] text-zinc-500">
                              id: {it?.id}
                            </div>
                          </div>
                        </div>

                        {/* ação opcional */}
                        {String(it?.tipo || '').toUpperCase() === 'IMOVEL' &&
                          it?.id && (
                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={() => onOpenImovel?.(it.id)}
                                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                              >
                                Abrir imóvel
                                <Home className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* pendências & justificativas */}
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-zinc-700" />
                  <div className="text-sm font-extrabold text-zinc-900">
                    Pendências & Justificativas
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                {/* torna info */}
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-extrabold text-zinc-900">
                      Torna
                    </div>
                    <div className="text-sm font-extrabold text-emerald-700">
                      {moneyBRL(vm.torna.total)}
                    </div>
                  </div>

                  <div className="mt-1 text-[12px] text-zinc-700">
                    Entrada mínima exigida:{' '}
                    <span className="font-semibold">
                      {moneyBRL(vm.torna.entradaMin)}
                    </span>
                  </div>

                  <div className="mt-1 text-[12px] text-zinc-700">
                    Entrada mínima OK:{' '}
                    <span className="font-semibold">
                      {vm.torna.entradaOk ? 'Sim' : 'Não'}
                    </span>
                  </div>
                </div>

                {/* pendências */}
                <div>
                  <div className="mb-2 text-xs font-extrabold text-zinc-900">
                    Pendências
                  </div>

                  {vm.pendencias.length === 0 ? (
                    <div className="rounded-xl bg-zinc-50 p-3 text-[12px] text-zinc-700">
                      Sem pendências registradas ✅
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {vm.pendencias.map((p, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
                        >
                          {p?.mensagem || p}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* justificativas */}
                <div>
                  <div className="mb-2 text-xs font-extrabold text-zinc-900">
                    Justificativas
                  </div>

                  {vm.justificativas.length === 0 ? (
                    <div className="rounded-xl bg-zinc-50 p-3 text-[12px] text-zinc-700">
                      Sem justificativas.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {vm.justificativas.map((j, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-[12px] text-zinc-800"
                        >
                          {j}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-200 bg-zinc-50 px-5 py-3">
          <div className="text-[12px] text-zinc-600">
            {vm.tipo === 'TRIANGULAR'
              ? 'Fluxo: B compra C, C paga A, A entrega o imóvel para B.'
              : 'Fluxo: B paga parte com item + completa com dinheiro/financiamento.'}
          </div>

          <button
            type="button"
            onClick={() => onClose?.()}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-800 hover:bg-zinc-50"
          >
            Fechar
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
